/**
 * @deprecated Superseded by usePermissions() / RequirePermission / AuthorizedRoute
 * `permission` prop. The page-permission system is being retired in favor of
 * verb-level permissions backed by dbo.ItemProperties (Matt Holton 2026-05-04).
 *
 * Status: kept live during cutover so existing pageKey-granted users don't
 * lose access via AuthorizedRoute's OR-logic. Removed in a follow-up cleanup
 * PR after dbo.ItemProperties grants are in place for all relevant users.
 *
 * New code: use `usePermissions()` / `<RequirePermission permission="UserCanXxx">`.
 */
import { createContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useFeatureFlags } from './useFeatureFlags';
import { pagePermissionApi, type MyPermissionsResponse } from '../services/pagePermissionApi';

export interface PagePermissionContextValue {
  pages: Record<string, boolean>;
  isLoading: boolean;
  hasPageAccess: (pageKey: string) => boolean;
}

const defaultValue: PagePermissionContextValue = {
  pages: {},
  isLoading: true,
  hasPageAccess: () => true, // default: allow all
};

// eslint-disable-next-line react-refresh/only-export-components
export const PagePermissionContext = createContext<PagePermissionContextValue>(defaultValue);

export function PagePermissionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const flags = useFeatureFlags();

  const { data, isLoading } = useQuery<MyPermissionsResponse>({
    queryKey: ['page-permissions-me', user?.userId],
    queryFn: () => pagePermissionApi.getMyPermissions(),
    enabled: isAuthenticated && flags.security,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // If security feature is disabled or not authenticated, allow all pages
  const pages = (!flags.security || !isAuthenticated || !data)
    ? {} // empty = hasPageAccess returns true for everything
    : data.pages;

  const hasPageAccess = (pageKey: string): boolean => {
    // If security feature flag is off, or no data loaded yet, allow all
    if (!flags.security || !data) return true;
    // If page key not in the map, default to true (unknown pages are accessible)
    return pages[pageKey] ?? true;
  };

  return (
    <PagePermissionContext.Provider value={{ pages, isLoading, hasPageAccess }}>
      {children}
    </PagePermissionContext.Provider>
  );
}

